import { useState } from 'react';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { Breakpoints } from 'utils/const.styles';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/ProgressWizard.styled';

/**
 * @author @buunsal
 * @description ProgressWizard component displays a step-by-step progress indicator for a multi-stage process.
 * It shows the current stage and allows navigation through stages.
 * It can be used in both desktop and mobile views, adapting its layout accordingly.
 * 
 * @param {Object} props
 * @param {Object[]} props.stages - Array of stages
 * @param {Number} props.index - Current index of the wizard
 * @param {Function} props.setIndex - Function to set index of current stage
 * @param {String} [props.className] - Additional class name for styling
 * @param {Number} [props.clickable = true] - If true, the steps are clickable
 * @param {Boolean} [props.gutterBottom] - If true, adds bottom margin
 * 
 * @returns {JSX.Element} ProgressWizard component
 * @example 
 * <ProgressWizard
	stages={[
	{ name: 'customerDetails'}
	{ name: 'orderDetails'}
	]}
	index={tabIndex}
	setIndex={()=>setTabIndex(1)}
	/>
 *
 */
const ProgressWizard = (props) => {
	const { stages, index, setIndex, className, clickable = false, gutterBottom } = props;

	const [currentIndex, setCurrentIndex] = useState(index ?? 0);

	useUpdateEffect(() => {
		setCurrentIndex(index);
	}, [index]);

	const tabletView = useMediaQuery(Breakpoints.tablet);

	const formatPageNumber = (num) => (num < 10 ? `0${num}` : num);

	return (
		<S.Container className={`progressWizard ${gutterBottom ? 'gutterBottom' : ''} ${className ?? ''}`}>
			{!tabletView ? (
				stages?.map((stage, i) => (
					<S.Stage
						key={i}
						className={`step-${i} ${clickable ? 'clickable' : ''}`}
						onClick={() => {
							clickable && setIndex(i);
						}}>
						<S.IconContainer
							className={
								(currentIndex === i && stage.success) || currentIndex > i
									? 'completed'
									: currentIndex === i
										? 'active'
										: ''
							}>
							{(currentIndex === i && stage.success) || currentIndex > i ? (
								<Icon icon={faCheck} size="medium" />
							) : (
								<Typography
									fontWeight="500"
									color={currentIndex < i ? 'var(--content-4)' : 'var(--alwaysWhiteColor)'}>
									{i + 1}
								</Typography>
							)}
						</S.IconContainer>
						<Typography
							css={S.LabelText}
							fontWeight={currentIndex === i ? '700' : '500'}
							color={currentIndex === i ? 'var(--black)' : 'var(--content-4)'}>
							{stage?.name}
						</Typography>
					</S.Stage>
				))
			) : (
				<S.MobileOuterDiv className="wizardMobile">
					<S.StageMobile>
						<Typography noWrap variant="p2" fontWeight="500" color="var(--black)" className="name">
							{stages && stages[index]?.name}
						</Typography>

						<S.MobilePageNumber>
							<Typography variant="p2" className="active" fontWeight="800" color="var(--black)">
								{formatPageNumber(index + 1)}
							</Typography>
							<Typography variant="p2" className="active" fontWeight="500" color="var(--content-4)">
								/{formatPageNumber(stages?.length)}
							</Typography>
						</S.MobilePageNumber>
					</S.StageMobile>
					{stages?.[index]?.description && (
						<Typography noWrap variant="caption" color="var(--content-3)" className="description">
							{stages[index].description}
						</Typography>
					)}
					<div style={{ display: 'flex', gap: '1rem' }}>
						{stages?.map((stage, i) => (
							<S.DivContainer
								key={i}
								className={
									(currentIndex === i && stage.success) || currentIndex > i
										? 'completed'
										: currentIndex === i
											? 'active'
											: ''
								}
							/>
						))}
					</div>
				</S.MobileOuterDiv>
			)}
		</S.Container>
	);
};

export default ProgressWizard;
